import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import AirportDropdownOption from './AirportDropdownOption'
import NoAirportsOption from './NoAirportsOption'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import { LuxKitModalBehaviourStateContext } from 'LuxKit/Modal/context/LuxKitModalBehaviourContexts'
import Heading from 'LuxKit/Typography/Heading'
import SearchFormModalTextField from 'components/SearchV2/Components/SearchFormField/SearchFormModalTextField'
import useAirportSearch from 'hooks/Flights/useAirportSearch'
import usePopularAirports from 'hooks/Flights/usePopularAirports'
import noop from 'lib/function/noop'
import { getAirportDisplayText } from 'lib/flights/flightUtils'

const Results = styled.div`
  position: relative;
  min-height: ${rem(80)};
`

const InputArea = styled(Pane)`
  position: sticky;
  top: 0;
  padding: ${rem(12)} ${rem(16)};
  margin: 0 ${rem(-16)};
  z-index: 1;
`

interface Props {
  className?: string
  onSelectAirport: (airport?: App.AirportLocation) => void
  onChange?: (text?: string) => void
  required?: boolean
  requiredErrorMessage?: string
  placeholder?: string
  /**
   * The selected airport for this control
   */
  airport?: App.AirportLocation
  /**
   * The airport going the *other direction*. Will be excluded from search results
   */
  alternateAirport?: App.AirportLocation
  fallbackHeading?: string
  direction: 'departure' | 'arrival'
  fallbackAirports?: Array<App.AirportLocation>
}

function FlightSearchAirportModalSelect(props: Props) {
  const {
    airport,
    alternateAirport,
    placeholder: placeholderProp,
    className,
    onSelectAirport,
    onChange = noop,
    required,
    requiredErrorMessage,
    fallbackHeading: fallbackHeadingProp,
    direction,
    fallbackAirports,
  } = props

  const intl = useIntl()

  const placeholder = useMemo(
    () =>
      placeholderProp ??
      intl.formatMessage({
        id: 'gxYXhH4sff_fchOXLh1x',
        defaultMessage: 'Search by city or airport',
        description: 'Placeholder text for airport search input field on the form',
      }),
    [intl, placeholderProp],
  )

  const fallbackHeading = useMemo(
    () =>
      fallbackHeadingProp ??
      intl.formatMessage({
        id: 'DqKsnAKyUTfgg8LD8Q1s',
        defaultMessage: 'Popular airports',
        description: 'Section heading for popular airports section in the flights section',
      }),
    [intl, fallbackHeadingProp],
  )

  const errorMessage = useMemo(() => {
    if (requiredErrorMessage) {
      return requiredErrorMessage
    }
    return direction === 'arrival'
      ? intl.formatMessage({
          id: 'dlz6xaTWNySG3teE_8DG',
          defaultMessage: 'Please enter a destination',
          description: 'Error message when destination airport is required but not provided',
        })
      : intl.formatMessage({
          id: 'ZH-RcFCGA2p4h9-7nehr',
          defaultMessage: 'Please enter a departure',
          description: 'Error message when departure airport is required but not provided',
        })
  }, [intl, requiredErrorMessage, direction])

  const loadingText = useMemo(
    () =>
      intl.formatMessage({
        id: 'OiDILwGHc_0aFvlNOELx',
        defaultMessage: 'Loading',
        description: 'Loading indicator text for airport search in the search interface',
      }),
    [intl],
  )

  const popularAirports = usePopularAirports(direction, { excludeAirport: alternateAirport })

  const [searchPhrase, setSearchPhrase] = useState<string | undefined>()
  const [airports, loading] = useAirportSearch(searchPhrase, { excludedAirport: alternateAirport })

  useEffect(() => {
    if (airport) {
      setSearchPhrase(getAirportDisplayText(airport))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onTextChange = useCallback((text?: string) => {
    setSearchPhrase(text)
    onChange(text)
  }, [onChange])

  const onClear = useCallback(() => {
    setSearchPhrase(undefined)
    onSelectAirport(undefined)
  }, [onSelectAirport])

  const showSearch = !!(searchPhrase && searchPhrase.length > 2)
  const noAirportOptions = !loading && airports.length === 0 && showSearch
  const airportList = showSearch ? airports : fallbackAirports || popularAirports
  const { bodyHasReachedStart } = useContext(LuxKitModalBehaviourStateContext)

  return (
    <div className={className}>
      <InputArea shadow={!bodyHasReachedStart ? 'bottom-small' : undefined}>
        <SearchFormModalTextField
          value={getAirportDisplayText(airport)}
          placeholder={placeholder}
          required={required}
          searchPhrase={searchPhrase}
          requiredErrorMessage={errorMessage}
          onChange={onTextChange}
          onClear={onClear}
          autoFocus
          autoSelect
        />
      </InputArea>
      <Results>
        <LoadingIndicator visible={loading} floating position="top">
          {loadingText}
        </LoadingIndicator>
        {!showSearch && <Heading variant="heading6">{fallbackHeading}</Heading>}
        {!loading && airportList.map(airport =>
          <AirportDropdownOption
            key={airport.id}
            subRecord={airport.isCityAirport}
            airport={airport}
            onSelectAirport={onSelectAirport}
          />,
        )}
        {noAirportOptions && <NoAirportsOption inputValue={searchPhrase} />}
      </Results>
    </div>
  )
}

export default FlightSearchAirportModalSelect
